import { httpClient } from '~/utils/axios'
import _ from 'lodash'
import { dayjsTz as dayjs } from '~/utils/dayjs'
import { syncVisibility, changeVisibility } from '~/utils/calendarVisibility'

const getDefaultState = () => {
  return {
    // contact
    firstContact: false,

    // auth
    authToken: null,
    headquarter: null,
    company: null,
    staff: null,
    settings: [],
    // 通知方式の切替日（サーバーENV由来 / 'YYYY-MM-DD' or null）。設定画面の注意書き表示に使う
    notification_cutover_date: null,
    new_alert_size: 0,

    // push
    lastUpdatedAt: null,
    oneSignalDeviseToken: null,

    // 端末固有設定
    latest_view_clients: [], //
    selectedClient: null,
    calendar_ids: [],
    calendar_view_type: null, // 未保存は null（初期表示は utils/calendarInitialView.js で PC/スマホ別に決める）
    calendar_only_business_hours: true,
    calendar_show_extra_days: true,
    calendar_start_on_sunday: true,
    calendar_resource_mode: 'calendar',
    calendar_duration: 15,
    calendar_selected_menu_ids: [], // イベント用メニューフィルター（共通化）
    // 表示設定は「隠す一覧」が正（utils/calendarVisibility.js）。calendar_ids /
    // calendar_selected_menu_ids は、隠す一覧から求めた「いま表示するもの」
    calendar_hidden_ids: [], // 表示するスタッフで外したもの
    calendar_seen_ids: [], // 一度でも見たスタッフ（旧設定の移行判定用）
    calendar_hidden_menu_ids: [], // 表示するイベントメニューで外したもの
    calendar_seen_menu_ids: [], // 一度でも見たイベントメニュー（旧設定の移行判定用）
    calendar_show_draft_events: true, // 下書きイベント表示（共通化）
    calendar_show_lessons: true, // セッション表示
    calendar_show_events: true, // イベント表示
    calendar_month_show_all_events: false, // 月表示で全件表示
    calendar_month_max_events: 8, // 月表示の最大表示件数
    reverve_check_has_ticket: false,
    reserve_push: false,
    showEventSetDetail: false,
    lessonTrainingEventAutoSave: false,

    // 事業者共通
    selected_all_calendars: [],

    latest_clients: [], // old

  }
}

const shouldUpdate = (apiUpdate, localUpdate) => {
  if (localUpdate === null) return false;
  if (apiUpdate === null) return false;

  // 更新日時を比較して、APIの方が新しければ更新が必要
  return dayjs(apiUpdate).isAfter(dayjs(localUpdate));
};

// 更新が必要なAPIリクエストを非同期で順番に実行
async function updateMastersSequentially(mu, lu, force = false) {
  const requests = [];
  // 条件に応じて実行する関数を配列に追加
  if (force || !lu || shouldUpdate(mu.calendars, lu.calendars)) requests.push(() => window.storeCtl.dispatch('company/getCalendars'));
  if (force || !lu || shouldUpdate(mu.resources, lu.resources)) requests.push(() => window.storeCtl.dispatch('company/getResources'));
  if (force || !lu || shouldUpdate(mu.courses, lu.courses)) requests.push(() => window.storeCtl.dispatch('company/getCourses'));
  if (force || !lu || shouldUpdate(mu.menus, lu.menus)) requests.push(() => window.storeCtl.dispatch('company/getMenus'));
  if (force || !lu || shouldUpdate(mu.payment_methods, lu.payment_methods)) requests.push(() => window.storeCtl.dispatch('company/getPaymentMethods'));
  if (force || !lu || shouldUpdate(mu.item_categoreis, lu.item_categoreis)) requests.push(() => window.storeCtl.dispatch('company/getItemCategories'));
  if (force || !lu || shouldUpdate(mu.items, lu.items)) requests.push(() => window.storeCtl.dispatch('company/getItems'));
  if (force || !lu || shouldUpdate(mu.training_categories, lu.training_categories)) requests.push(() => window.storeCtl.dispatch('company/getTrainingCategories'));
  if (force || !lu || shouldUpdate(mu.training_events, lu.training_events)) requests.push(() => window.storeCtl.dispatch('company/getTrainingEvents'));
  if (force || !lu || shouldUpdate(mu.training_machines, lu.training_machines)) requests.push(() => window.storeCtl.dispatch('company/getTrainingMachines'));
  if (force || !lu || shouldUpdate(mu.training_muscles, lu.training_muscles)) requests.push(() => window.storeCtl.dispatch('company/getTrainingMuscles'));
  if (force || !lu || shouldUpdate(mu.time_zones, lu.time_zones)) requests.push(() => window.storeCtl.dispatch('company/getTimeZones'));
  if (force || !lu || shouldUpdate(mu.surveys, lu.surveys)) requests.push(() => window.storeCtl.dispatch('company/getSurveys'));
  if (force || !lu || shouldUpdate(mu.company_groups, lu.company_groups)) requests.push(() => window.storeCtl.dispatch('company/getCompanyGroups'));
  if (force || !lu || shouldUpdate(mu.reserve_settings, lu.reserve_settings)) requests.push(() => window.storeCtl.dispatch('company/getReserveSettings'));
  if (force || !lu || shouldUpdate(mu.companies, lu.companies)) requests.push(() => window.storeCtl.dispatch('company/getCompanies'));

  // 非同期リクエストを順番に実行
  for (const request of requests) {
    await request(); // 各リクエストを待機してから次へ進む
  }
}

export const state = () => getDefaultState()
export const mutations = {
  // contact
  firstContactDone(state, payload) {
    state.firstContact = true
  },

  // auth
  setAuthToken(state, auth_token) {
    state.authToken = auth_token
  },
  setHeadquarter(state, headquarter) {
    state.headquarter = headquarter
  },
  setCompany(state, company) {
    state.company = company
  },
  setStaff(state, staff) {
    state.staff = staff
  },
  setSettings(state, settings) {
    state.settings = settings
  },
  setNotificationCutoverDate(state, notification_cutover_date) {
    state.notification_cutover_date = notification_cutover_date ?? null
  },
  setLastUpdatedAt(state, payload) {
    state.lastUpdatedAt = payload
  },
  setNewAlertSize(state, new_alert_size) {
    state.new_alert_size = new_alert_size
  },
  setOneSignalDeviseToken(state, oneSignalDeviseToken) {
    state.oneSignalDeviseToken = oneSignalDeviseToken
  },

  // 端末固有設定
  setClient(state, client) {
    console.log('setClient')
    state.selectedClient = client

    // リスト追加
    let new_clients = _.filter(state.latest_view_clients, cli => {
      return cli.uid !== client.uid
    })
    new_clients.unshift({
      uid: client.uid,
      name: client.name
    })
    // 20超えたら銭湯削除
    if (new_clients.length > 10) new_clients.splice(-1)
    state.latest_view_clients = new_clients
  },
  clearLatestClients(state) {
    state.latest_clients = []
  },
  setCalendarIds(state, calendar_ids) {
    state.calendar_ids = calendar_ids
  },
  setCalendarViewType(state, calendar_view_type) {
    state.calendar_view_type = calendar_view_type
  },
  setCalendarOnlyBusinessHours(state, calendar_only_business_hours) {
    state.calendar_only_business_hours = calendar_only_business_hours
  },
  setCalendarShowExtraDays(state, calendar_show_extra_days) {
    state.calendar_show_extra_days = calendar_show_extra_days
  },
  setCalendarStartOnSunday(state, calendar_start_on_sunday) {
    state.calendar_start_on_sunday = calendar_start_on_sunday
  },
  setCalendarResourceMode(state, calendar_resource_mode) {
    state.calendar_resource_mode = calendar_resource_mode
  },
  setCalendarDuration(state, calendar_duration) {
    state.calendar_duration = calendar_duration
  },
  setCalendarSelectedMenuIds(state, calendar_selected_menu_ids) {
    state.calendar_selected_menu_ids = calendar_selected_menu_ids
  },
  // 表示するスタッフ: 一覧（allIds）に合わせて、隠す一覧から表示するものを求め直す
  syncCalendarVisibility(state, allIds) {
    const r = syncVisibility({
      allIds,
      legacyVisibleIds: state.calendar_ids,
      hiddenIds: state.calendar_hidden_ids,
      seenIds: state.calendar_seen_ids
    })
    state.calendar_ids = r.visibleIds
    state.calendar_hidden_ids = r.hiddenIds
    state.calendar_seen_ids = r.seenIds
  },
  // 表示するスタッフ: チェックの付け外し
  setCalendarVisibility(state, { visibleIds, allIds }) {
    // 一覧の読み込み前（チェック欄が出ていない）の書き込みで保存値を潰さない
    if (!Array.isArray(allIds) || allIds.length === 0) return
    const r = changeVisibility({
      allIds,
      visibleIds,
      hiddenIds: state.calendar_hidden_ids,
      seenIds: state.calendar_seen_ids
    })
    state.calendar_ids = r.visibleIds
    state.calendar_hidden_ids = r.hiddenIds
    state.calendar_seen_ids = r.seenIds
  },
  // 表示するイベントメニュー: 一覧に合わせて求め直す
  syncCalendarMenuVisibility(state, allIds) {
    const r = syncVisibility({
      allIds,
      legacyVisibleIds: state.calendar_selected_menu_ids,
      hiddenIds: state.calendar_hidden_menu_ids,
      seenIds: state.calendar_seen_menu_ids
    })
    state.calendar_selected_menu_ids = r.visibleIds
    state.calendar_hidden_menu_ids = r.hiddenIds
    state.calendar_seen_menu_ids = r.seenIds
  },
  // 表示するイベントメニュー: チェックの付け外し・すべて表示・すべてオフ
  setCalendarMenuVisibility(state, { visibleIds, allIds }) {
    // 一覧の読み込み前（チェック欄が出ていない）の書き込みで保存値を潰さない
    if (!Array.isArray(allIds) || allIds.length === 0) return
    const r = changeVisibility({
      allIds,
      visibleIds,
      hiddenIds: state.calendar_hidden_menu_ids,
      seenIds: state.calendar_seen_menu_ids
    })
    state.calendar_selected_menu_ids = r.visibleIds
    state.calendar_hidden_menu_ids = r.hiddenIds
    state.calendar_seen_menu_ids = r.seenIds
  },
  setCalendarShowDraftEvents(state, calendar_show_draft_events) {
    state.calendar_show_draft_events = calendar_show_draft_events
  },
  setCalendarShowLessons(state, calendar_show_lessons) {
    state.calendar_show_lessons = calendar_show_lessons
  },
  setCalendarShowEvents(state, calendar_show_events) {
    state.calendar_show_events = calendar_show_events
  },
  setCalendarMonthShowAllEvents(state, calendar_month_show_all_events) {
    state.calendar_month_show_all_events = calendar_month_show_all_events
  },
  setCalendarMonthMaxEvents(state, calendar_month_max_events) {
    state.calendar_month_max_events = calendar_month_max_events
  },
  setReverveCheckHasTicket(state, reverve_check_has_ticket) {
    state.reverve_check_has_ticket = reverve_check_has_ticket
  },
  setReservePush(state, reserve_push) {
    state.reserve_push = reserve_push
  },
  setShowEventSetDetail(state, showEventSetDetail) {
    state.showEventSetDetail = showEventSetDetail
  },
  setLessonTrainingEventAutoSave(state, lessonTrainingEventAutoSave) {
    state.lessonTrainingEventAutoSave = lessonTrainingEventAutoSave
  },
  
  // 事業者共通
  setSelectedAllCalendars(state, selected_all_calendars) {
    state.selected_all_calendars = selected_all_calendars
  },

  // logout
  logout(state) {
    $nuxt.$router.push(`/login/`)
    Object.assign(state, getDefaultState())
    window.storeCtl.commit('company/reset')
    window.storeCtl.commit('devise/reset')
    window.storeCtl.commit('announcement/reset')
  },

  // update old
  updateOldDate(state) {
    state.latest_clients = []
  }
}
export const getters = {
  isLogin(state) {
    return !!state.authToken
  },
  isOwner(state) {
    return state.staff.id === state.headquarter.owner_id
  }
}

// 戻り地はisBeforeRequest
export const actions = {
  async refreshToken(context) {
    await httpClient
      .post('/cmp/account/refresh_token.json')
      .then(async res => {
        if (res.data.status === 'success') {
          if(res.data.data.is_admin === false) context.commit('setAuthToken', res.data.data.auth_token)
          console.log("refreshToken",res.data.data.expire_at)
        } else {
          window.storeCtl.commit('alert/setError', res.data.message)
        }
      })
  },
  // マスタを矯正Update
  async mstForceUpdate(context) {
    window.storeCtl.dispatch('auth/getStaff')
    window.storeCtl.dispatch('auth/getCompany')
    updateMastersSequentially({}, {}, true)
  },
  // 更新状況を見てマスタ更新
  async mstCheckUpdate(context) {
    await httpClient
      .get('/cmp/has_update.json')
      .then(async res => {
        if (res.data.status === 'success') {          
          // 最終更新日時
          let lastUpdatedAt = context.state.lastUpdatedAt;
          // それぞれの更新日時が lastUpdatedAt より新しいかをチェック
          let isNewer = lastUpdatedAt === null ||
                      dayjs(res.data.headquarter_updated_at).isAfter(dayjs(lastUpdatedAt)) ||
                      dayjs(res.data.company_updated_at).isAfter(dayjs(lastUpdatedAt)) ||
                      dayjs(res.data.staff_updated_at).isAfter(dayjs(lastUpdatedAt));
          // 更新されている場合
          if (isNewer) {
            // 各更新日時を dayjs で処理
            let updateTimes = [
              dayjs(res.data.headquarter_updated_at),
              dayjs(res.data.company_updated_at),
              dayjs(res.data.staff_updated_at)
            ];
            // 最も古い日時を取得
            let oldestUpdate = dayjs.min(updateTimes);
            context.commit('setLastUpdatedAt', oldestUpdate)
            // ここで一度マスターをすべて取得
            window.storeCtl.dispatch('auth/getStaff')
            window.storeCtl.dispatch('auth/getCompany')
          }

          // マスター更新必要性チェック
          const mu = res.data.data.master_updates
          const lu = window.storeCtl.state.company.local_master_updates
          updateMastersSequentially(mu, lu)
          // FIXME: どれかのAPIコケてもマスタ更新されない気がする
          window.storeCtl.commit('company/setMasterUpdates', mu)
        } else {
          window.storeCtl.commit('alert/setError', res.data.message)
        }
      })
      .finally(() => {
        window.storeCtl.commit('loading/stop')
      })
  },

  //// push
  // ログインしていて
  // LocalStrage取れていて
  // StoreとLocalstrageの値が違う
  // サーバー同期処理
  async updateOneSignalDeviseToken(context) {
    const onesignal_user_id = localStorage.getItem('onesignal_user_id')
    if (
      onesignal_user_id &&
      window.storeCtl.state.auth.oneSignalDeviseToken !== onesignal_user_id
    ) {
      await httpClient
        .post('/cmp/account/update_onesignal_device_token.json', {
          onesignal_device_token: onesignal_user_id
        })
        .then(res => {
          if (res.data.status === 'success') {
            window.storeCtl.commit(
              'auth/setOneSignalDeviseToken',
              onesignal_user_id
            )
          } else {
            window.storeCtl.commit('alert/setError', res.data.message)
          }
        })
    }
  },

  async getClient(context, uid) {
    await httpClient
      .get(`/cmp/floor/clients/${uid}.json`)
      .then(async res => {
        if (res.data.status === 'success') {
          context.commit('setClient', res.data.data.client)
        } else {
          window.storeCtl.commit('alert/setError', res.data.message)
        }
      })
      .finally(() => {
        window.storeCtl.commit('loading/stop')
      })
  },

  async getStaff(context) {
    await httpClient
      .get('/cmp/account/staff.json')
      .then(async res => {
        if (res.data.status === 'success') {
          context.commit('setStaff', res.data.data.staff)
          window.storeCtl.commit("devise/setAbleCompanies", res.data.data.able_companies)
        } else {
          window.storeCtl.commit('alert/setError', res.data.message)
        }
      })
      .finally(() => {})
  },
  async getCompany(context) {
    await httpClient
      .get('/cmp/account/company.json')
      .then(async res => {
        if (res.data.status === 'success') {
          context.commit('setHeadquarter', res.data.data.headquarter)
          context.commit('setCompany', res.data.data.company)
          context.commit('setSettings', res.data.data.settings)
          context.commit(
            'setNotificationCutoverDate',
            res.data.data.notification_cutover_date
          )
        } else {
          window.storeCtl.commit('alert/setError', res.data.message)
        }
      })
      .finally(() => {})
  },
}
